import moment from 'moment';
import { timeFilters } from './constants';
import { groupBy } from 'lodash';
import { IRootStore } from '@stores/index';
import { TrackingPlanData, TrackingPlanPeriodData, TrackingPlanResponseData } from './types';
import { TimeFilterValue } from '@components/common/types';

export const getTimeParams = (
  timeFilter: TimeFilterValue,
  type: 'current' | 'previous' = 'current',
) => {
  const { dayCount } = timeFilters[timeFilter];

  const start =
    type === 'current'
      ? moment.utc().subtract(dayCount, 'days').toISOString()
      : moment
          .utc()
          .subtract(dayCount * 2, 'days')
          .toISOString();
  const end =
    type === 'current'
      ? moment.utc().toISOString()
      : moment.utc().subtract(dayCount, 'days').toISOString();

  return {
    start,
    end,
  };
};

export const convertToPercentage = (successSum: number, abortSum: number): number => {
  if (abortSum === 0 && successSum === 0) {
    return 0;
  }
  const percentage = (abortSum * 100) / (successSum + abortSum);
  const precision = 100; // 2 decimal places
  const roundedNumber = Math.round((percentage + Number.EPSILON) * precision) / precision;
  return roundedNumber;
};

export const getPercentChange = (
  currentValue: number,
  pastValue: number,
  isPercentage: boolean,
) => {
  if (isPercentage) {
    return currentValue - pastValue;
  }
  if (pastValue === 0) {
    if (currentValue === 0) {
      return 0;
    }
    return 100;
  }
  return ((currentValue - pastValue) / pastValue) * 100;
};

export const getRoundedValue = (value: number, decimalPlaces: number): string => {
  const precision = 10 ** decimalPlaces;
  const roundedNumber = Math.abs(Math.round((value + Number.EPSILON) * precision) / precision);
  return `${roundedNumber === value ? '' : '~'}${roundedNumber}`;
};

export const transformTrackingPlansData = (
  connections: TrackingPlanResponseData,
  { sourcesListStore, trackingPlanListStore }: IRootStore,
) => {
  const sources = connections.map((connection) => {
    const source = sourcesListStore.sourceById(connection.sourceId)!;
    const trackingPlan = trackingPlanListStore.getById(connection.trackingPlanId)!;
    return {
      id: `${source.id}-${trackingPlan.id}`,
      source,
      trackingPlanId: connection.trackingPlanId,
      trackingPlanName: trackingPlan.name,
      trackingPlanVersion: trackingPlan.version,
      eventsValidated: connection.eventsValidated,
      violationCount: connection.violationCount,
      eventsWithViolations: connection.eventsWithViolations,
      isActive: source.trackingPlanConfig?.trackingPlanId === connection.trackingPlanId,
    };
  });
  const sourcesWithViolation = sources.filter((source) => source.violationCount > 0);

  const sourcesCount = Object.keys(groupBy(sourcesWithViolation, 'id')).length;
  return {
    overview: {
      total: sources.reduce((acc, source) => acc + source.eventsWithViolations, 0),
      sourcesCount,
      violationCount: sources.reduce((acc, source) => acc + source.violationCount, 0),
    },
    sources,
  };
};

export const constructTrackingPlanPeriodData = (
  current: TrackingPlanData[] = [],
  prev: TrackingPlanData[] = [],
): TrackingPlanPeriodData[] =>
  current.map((currentTrackingPlan) => {
    const prevTrackingPlan = prev?.find(
      (prevTrackingPlan) => prevTrackingPlan.id === currentTrackingPlan.id,
    );
    return {
      ...currentTrackingPlan,
      prevEventsValidated: prevTrackingPlan?.eventsValidated || 0,
      prevViolationCount: prevTrackingPlan?.violationCount || 0,
    };
  });
